import { angleDifference, bearing, clamp, distance, linePoint, lineSlice } from "./geo";
import type { Anchor, Coordinate, GraphSummary, PlannedRoute, RouteEvent, SimulationDataset, SimulationEdge, VisualFeature } from "./types";

export interface Arc {
    id: string; edgeId: string; from: string; to: string; length: number;
    coordinates: Coordinate[]; heading: number; name: string;
}
interface SearchTree { start: Anchor; costs: Map<string, number>; previous: Map<string, string>; limited: boolean }
export interface Transition { meters: number; skipped: number; courseChange: number; arcs: string[] }
interface VisibilityRegion {
    segments: { arcId: string; from: number; to: number }[];
    geometry: { type: "MultiLineString"; coordinates: Coordinate[][] };
    limited: boolean;
}

class MinHeap {
    private values: { id: string; cost: number }[] = [];
    push(value: { id: string; cost: number }) {
        this.values.push(value);
        let index = this.values.length - 1;
        while (index > 0) {
            const parent = (index - 1) >> 1;
            if (this.values[parent].cost <= value.cost) break;
            this.values[index] = this.values[parent]; index = parent;
        }
        this.values[index] = value;
    }
    pop() {
        const first = this.values[0], last = this.values.pop();
        if (!first || !last || !this.values.length) return first;
        let index = 0;
        while (index * 2 + 1 < this.values.length) {
            let child = index * 2 + 1;
            if (child + 1 < this.values.length && this.values[child + 1].cost < this.values[child].cost) child++;
            if (this.values[child].cost >= last.cost) break;
            this.values[index] = this.values[child]; index = child;
        }
        this.values[index] = last;
        return first;
    }
}

export function featureKey(feature: VisualFeature): string {
    return `${feature.kind}|${feature.signCode.trim().replace(/^UA:/i, "")}|${feature.signValue.trim().toLowerCase()}`;
}

export class RoadGraph {
    readonly arcs = new Map<string, Arc>();
    readonly outgoing = new Map<string, Arc[]>();
    private incoming = new Map<string, Arc[]>();
    readonly anchors: Anchor[] = [];
    readonly anchorById = new Map<string, Anchor>();
    private anchorsOnArc = new Map<string, Anchor[]>();
    private cache = new Map<string, SearchTree>();
    private visibilityCache = new Map<string, VisibilityRegion>();

    constructor(readonly dataset: SimulationDataset) {
        if (dataset.edges.length > 20000 || dataset.landmarks.length > 1000) throw new Error("Набір завеликий для браузерного експерименту. Звузьте область.");
        const edges = new Map<string, SimulationEdge>();
        for (const edge of dataset.edges) {
            if (!(edge.lengthMeters > 0) || !Number.isFinite(edge.lengthMeters) || edge.geometry.coordinates.length < 2 || edges.has(edge.id)) throw new Error("Некоректна геометрія дорожнього набору.");
            edges.set(edge.id, edge);
            for (const direction of [1, -1] as const) {
                if (edge.oneWay !== 0 && edge.oneWay !== direction) continue;
                const coordinates = edge.geometry.coordinates.map(point => [point[0], point[1]] as Coordinate);
                if (direction === -1) coordinates.reverse();
                const arc: Arc = { id: `${edge.id}/${direction === 1 ? "f" : "b"}`, edgeId: edge.id,
                    from: direction === 1 ? edge.from : edge.to, to: direction === 1 ? edge.to : edge.from,
                    coordinates, length: edge.lengthMeters, heading: bearing(coordinates[0], coordinates[coordinates.length - 1]), name: edge.name };
                this.arcs.set(arc.id, arc);
                const outgoing = this.outgoing.get(arc.from) ?? [];
                outgoing.push(arc); this.outgoing.set(arc.from, outgoing);
                const incoming = this.incoming.get(arc.to) ?? [];
                incoming.push(arc); this.incoming.set(arc.to, incoming);
            }
        }
        const raw = new Map<string, { offset: number; landmarkId: string; feature: VisualFeature; accuracy: number }[]>();
        for (const landmark of dataset.landmarks) {
            for (const binding of landmark.bindings) {
                const edge = edges.get(binding.edgeId);
                if (!edge || binding.offsetMeters < -0.1 || binding.offsetMeters > edge.lengthMeters + 0.1) continue;
                for (const direction of ["f", "b"] as const) {
                    if ((binding.direction === "forward" && direction === "b") || (binding.direction === "backward" && direction === "f")) continue;
                    const arcId = `${edge.id}/${direction}`;
                    if (!this.arcs.has(arcId)) continue;
                    const items = raw.get(arcId) ?? [];
                    items.push({ offset: clamp(direction === "f" ? binding.offsetMeters : edge.lengthMeters - binding.offsetMeters, 0, edge.lengthMeters),
                        landmarkId: landmark.id, feature: { kind: landmark.kind, signCode: landmark.signCode, signValue: landmark.signValue },
                        accuracy: Math.max(landmark.estimatedBinding ? 35 : 0, landmark.accuracyMeters ?? 15) });
                    raw.set(arcId, items);
                }
            }
        }
        for (const [arcId, items] of raw) {
            const arc = this.arcs.get(arcId)!;
            const groups: typeof items[] = [];
            for (const item of items.sort((a, b) => a.offset - b.offset || a.landmarkId.localeCompare(b.landmarkId))) {
                const last = groups[groups.length - 1];
                if (last && item.offset - last[0].offset <= 3) last.push(item);
                else groups.push([item]);
            }
            for (const group of groups) {
                const offset = group.reduce((sum, item) => sum + item.offset, 0) / group.length;
                const landmarkIds = [...new Set(group.map(item => item.landmarkId))].sort();
                const features = [...new Map(group.map(item => [item.landmarkId, item.feature])).values()];
                const anchor: Anchor = { id: `${arcId}@${landmarkIds.join("+")}`, arcId, offset, landmarkIds, features,
                    coordinate: linePoint(arc.coordinates, offset / arc.length), heading: arc.heading, roadName: arc.name,
                    accuracyMeters: Math.max(...group.map(item => item.accuracy)) };
                this.anchors.push(anchor); this.anchorById.set(anchor.id, anchor);
                const anchors = this.anchorsOnArc.get(arcId) ?? [];
                anchors.push(anchor); this.anchorsOnArc.set(arcId, anchors);
            }
        }
        this.anchors.sort((a, b) => a.id.localeCompare(b.id));
    }

    search(start: Anchor, maxMeters = 3000): SearchTree {
        const key = `${start.id}:${Math.ceil(maxMeters / 100) * 100}`;
        const cached = this.cache.get(key);
        if (cached) return cached;
        const limit = Math.ceil(maxMeters / 100) * 100;
        // Costs are measured at the START of an oriented edge. The initial edge
        // uses a negative offset, so its remaining portion is handled exactly.
        const costs = new Map([[start.arcId, -start.offset]]);
        const previous = new Map<string, string>();
        const queue = new MinHeap(); queue.push({ id: start.arcId, cost: -start.offset });
        let visited = 0, limited = false;
        while (true) {
            const current = queue.pop();
            if (!current) break;
            if (current.cost !== costs.get(current.id)) continue;
            if (++visited > 40000) { limited = true; break; }
            const arc = this.arcs.get(current.id)!;
            const cost = current.cost + arc.length;
            if (cost > limit) continue;
            for (const next of this.outgoing.get(arc.to) ?? []) {
                // No teleportation at crossings or immediate reversal on the
                // same segment. Explicit legal U-turn modeling is a later step.
                if (next.edgeId === arc.edgeId) continue;
                if (cost >= (costs.get(next.id) ?? Infinity)) continue;
                costs.set(next.id, cost); previous.set(next.id, current.id); queue.push({ id: next.id, cost });
            }
        }
        const tree = { start, costs, previous, limited };
        if (this.cache.size >= 12) this.cache.delete(this.cache.keys().next().value!);
        this.cache.set(key, tree);
        return tree;
    }

    transition(tree: SearchTree, to: Anchor, maxMeters: number): Transition | null {
        const cost = tree.costs.get(to.arcId);
        if (cost === undefined) return null;
        const meters = cost + to.offset;
        if (meters < 1 || meters > maxMeters) return null;
        const arcs = [to.arcId];
        while (arcs[0] !== tree.start.arcId) {
            const previous = tree.previous.get(arcs[0]);
            if (!previous || arcs.length > this.arcs.size) return null;
            arcs.unshift(previous);
        }
        const passed = new Set<string>();
        const endpoints = new Set([...tree.start.landmarkIds, ...to.landmarkIds]);
        for (const arcId of arcs) {
            for (const anchor of this.anchorsOnArc.get(arcId) ?? []) {
                if (arcId === tree.start.arcId && anchor.offset <= tree.start.offset + 3) continue;
                if (arcId === to.arcId && anchor.offset >= to.offset - 3) continue;
                for (const id of anchor.landmarkIds) if (!endpoints.has(id)) passed.add(id);
            }
        }
        return { meters, skipped: passed.size, courseChange: angleDifference(to.heading, tree.start.heading), arcs };
    }

    visibilityRegion(anchor: Anchor, rangeMeters: number): VisibilityRegion {
        const key = `${anchor.id}:${rangeMeters}`;
        const cached = this.visibilityCache.get(key);
        if (cached) return cached;
        const first = this.arcs.get(anchor.arcId)!;
        const segments = new Map<string, { arcId: string; from: number; to: number }>();
        const queue = [{ arc: first, end: anchor.offset, remaining: rangeMeters }];
        const visited = new Map<string, number>();
        let limited = false;
        while (queue.length) {
            const { arc, end, remaining } = queue.pop()!;
            if ((visited.get(arc.id) ?? -1) >= remaining) continue;
            visited.set(arc.id, remaining);
            const from = Math.max(0, end - remaining);
            const previous = segments.get(arc.id);
            if (end > from) segments.set(arc.id, { arcId: arc.id, from: Math.min(previous?.from ?? from, from), to: Math.max(previous?.to ?? end, end) });
            if (visited.size > 256) { limited = true; break; }
            if (remaining > end) for (const incoming of this.incoming.get(arc.from) ?? []) {
                if (incoming.edgeId !== arc.edgeId) queue.push({ arc: incoming, end: incoming.length, remaining: remaining - end });
            }
        }
        const result: VisibilityRegion = { segments: [...segments.values()], limited, geometry: { type: "MultiLineString",
            coordinates: [...segments.values()].map(segment => {
                const arc = this.arcs.get(segment.arcId)!;
                return lineSlice(arc.coordinates, segment.from / arc.length, segment.to / arc.length);
            }) } };
        if (this.visibilityCache.size >= 128) this.visibilityCache.delete(this.visibilityCache.keys().next().value!);
        this.visibilityCache.set(key, result);
        return result;
    }

    sightingTransition(tree: SearchTree, to: Anchor, rangeMeters: number, maxTravel: number): Transition | null {
        const source = tree.start;
        const signed = (tree.costs.get(to.arcId) ?? Infinity) + to.offset;
        if (signed >= -rangeMeters && signed <= maxTravel + rangeMeters) {
            const normal = signed >= 1 ? this.transition(tree, to, maxTravel + rangeMeters) : null;
            return { meters: signed, skipped: signed > rangeMeters ? normal?.skipped ?? 0 : 0, courseChange: 0, arcs: normal?.arcs ?? [] };
        }
        // Nearer objects may be noticed AFTER a more distant one. This is
        // not reverse vehicle movement: both are inside the visibility window.
        const reverse = this.search(to, rangeMeters);
        const behind = (reverse.costs.get(source.arcId) ?? Infinity) + source.offset;
        if (behind >= 0 && behind <= rangeMeters) return { meters: -behind, skipped: 0, courseChange: 0, arcs: [] };
        // Different branches can be visible from a common approach, but mere
        // Euclidean proximity (e.g. a bridge crossing) is not sufficient.
        const left = this.visibilityRegion(source, rangeMeters), right = this.visibilityRegion(to, rangeMeters);
        if (left.segments.some(a => right.segments.some(b => a.arcId === b.arcId && Math.max(a.from, b.from) <= Math.min(a.to, b.to)))) {
            return { meters: 0, skipped: 0, courseChange: 0, arcs: [] };
        }
        return null;
    }

    plan(fromId: string, toId: string): PlannedRoute {
        const from = this.anchorById.get(fromId), to = this.anchorById.get(toId);
        if (!from || !to) throw new Error("Виберіть початковий і кінцевий орієнтири.");
        const tree = this.search(from, 5000);
        if (tree.limited) throw new Error("Пошук маршруту досяг ліміту. Звузьте набір даних.");
        const transition = this.transition(tree, to, 5000);
        if (!transition) throw new Error("Немає шляху до 5 км у цьому напрямку. Перевірте односторонній рух, з’єднання та межі набору.");
        const events: RouteEvent[] = [], legs: PlannedRoute["legs"] = [];
        let cumulative = 0;
        for (let index = 0; index < transition.arcs.length; index++) {
            const arc = this.arcs.get(transition.arcs[index])!;
            const start = index === 0 ? from.offset : 0;
            const end = index === transition.arcs.length - 1 ? to.offset : arc.length;
            for (const anchor of this.anchorsOnArc.get(arc.id) ?? []) {
                if (anchor.offset < start - 0.1 || anchor.offset > end + 0.1) continue;
                const last = events[events.length - 1];
                // Multiple bindings of the same physical object at a junction
                // must not become multiple independent observations.
                if (last && anchor.landmarkIds.some(id => last.landmarkIds.includes(id)) && cumulative + anchor.offset - start - last.distanceAlong < 30) continue;
                events.push({ ...anchor, distanceAlong: cumulative + anchor.offset - start });
            }
            legs.push({ coordinates: lineSlice(arc.coordinates, start / arc.length, end / arc.length), lengthMeters: end - start, heading: arc.heading });
            cumulative += end - start;
        }
        if (events.length < 2) throw new Error("На цьому маршруті менше двох різних орієнтирів.");
        return { events, legs, coordinates: legs.flatMap((leg, index) => index ? leg.coordinates.slice(1) : leg.coordinates), lengthMeters: cumulative };
    }

    summary(): GraphSummary {
        let from: Anchor | undefined, to: Anchor | undefined, best = 0;
        const preset = this.dataset.presets[0];
        if (preset) {
            from = this.anchors.find(anchor => anchor.landmarkIds.includes(preset.fromLandmark) && anchor.arcId.endsWith("/f"));
            to = this.anchors.find(anchor => anchor.landmarkIds.includes(preset.toLandmark) && anchor.arcId.endsWith("/f"));
        } else {
            for (const start of this.anchors.slice(0, 30)) {
                const tree = this.search(start, 2000);
                for (const target of this.anchors) {
                    if (distance(start.coordinate, target.coordinate) < 30) continue;
                    const transition = this.transition(tree, target, 2000);
                    if (transition && transition.skipped + 2 > best) {
                        best = transition.skipped + 2; from = start; to = target;
                    }
                }
            }
        }
        return { anchors: this.anchors, suggestedFrom: from?.id ?? this.anchors[0]?.id ?? "", suggestedTo: to?.id ?? this.anchors[1]?.id ?? "" };
    }
}
